import { useState, useEffect, useRef, type CSSProperties } from "react";

interface ConfirmDeleteWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  /** Backing enum field the workflow owns — removed alongside the workflow. */
  workflowFieldName: string;
  tableName: string;
  /** Active row count — every row loses its workflow state. */
  rowCount: number;
  /** Performs the delete (single update_entity_type). Throws to surface an error. */
  onConfirm: () => Promise<void>;
}

/**
 * Sprint 14b — type-the-name confirmation for deleting a workflow. Because the
 * workflow OWNS its backing field (Q2), deleting it also drops that field and
 * the per-row state stored in it — strictly more destructive than a plain
 * workflow detach. Mirrors Sprint 14a's <ConfirmDeleteTableModal> convention:
 * name the exact data loss, require the user to type the field name to confirm.
 */
export function ConfirmDeleteWorkflowModal({
  open,
  onClose,
  workflowFieldName,
  tableName,
  rowCount,
  onConfirm,
}: ConfirmDeleteWorkflowModalProps) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      setError(null);
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const confirmed = typed.trim() === workflowFieldName;

  async function handleDelete() {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Above the workflow editor (zIndex 200) so it stacks on top.
    zIndex: 210,
    fontFamily: "var(--font-sans)",
  };
  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 440,
    maxWidth: "90vw",
  };
  const inputStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };
  const btnBase: CSSProperties = {
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: "var(--border-radius-md)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    border: "none",
  };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Delete workflow" style={modal}>
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 8px 0", color: "var(--color-text-primary)" }}>
          Delete the "{workflowFieldName}" workflow?
        </h2>
        <div style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          This removes the workflow from{" "}
          <strong style={{ color: "var(--color-text-primary)" }}>{tableName}</strong> and deletes the{" "}
          <strong style={{ color: "var(--color-text-primary)" }}>{workflowFieldName}</strong> field, permanently
          discarding the workflow state for{" "}
          <strong style={{ color: "var(--color-text-primary)" }}>
            {rowCount} {rowCount === 1 ? "row" : "rows"}
          </strong>
          . This cannot be undone.
        </div>

        <label style={{ display: "block", fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>
          Type <strong style={{ color: "var(--color-text-primary)" }}>{workflowFieldName}</strong> to confirm
        </label>
        <input
          ref={inputRef}
          type="text"
          style={inputStyle}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && confirmed) handleDelete();
            if (e.key === "Escape" && !busy) onClose();
          }}
          placeholder={workflowFieldName}
          aria-label="Workflow field name confirmation"
        />

        {error && <div style={{ fontSize: 13, color: "var(--c-red-600)", marginTop: 12 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ ...btnBase, background: "transparent", color: "var(--color-text-secondary)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!confirmed || busy}
            style={{
              ...btnBase,
              background: "var(--c-red-600)",
              color: "#fff",
              opacity: !confirmed || busy ? 0.5 : 1,
              cursor: !confirmed || busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Deleting..." : "Delete workflow"}
          </button>
        </div>
      </div>
    </div>
  );
}
